import React, { useState, useEffect } from 'react';
import { Table, Button, Select,DatePicker, Input, Space, Row, Col, message, Upload, Card, Tabs } from 'antd';
import { DownloadOutlined, FileExcelOutlined, UploadOutlined, FileDoneOutlined, CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../api/axios'; // Your axios instance

const { TabPane } = Tabs;
const { Option } = Select;

export default function GenSalesInvoice() {
    const [purchaseInvoices, setPurchaseInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [supplierFilter, setSupplierFilter] = useState('');
    const [invoiceIdFilter, setInvoiceIdFilter] = useState('');
    const [processing, setProcessing] = useState(false);
    const [processResults, setProcessResults] = useState(null);
    const [csvFileList, setCsvFileList] = useState([]);

    // for date filter
    const [dateFilter, setDateFilter] = useState(null);
    const [dateOperator, setDateOperator] = useState('>=');

    // urls for generated and merged pdfs
    const generatedFolderUrl = '/uploads/generated_pdfs/';
    const mergedFolderUrl    = '/uploads/merged_pdfs/';

    // for view and stream logs
    const outLogUrl   = '/gensalesinvoice/purchase-to-sales/logs/out';
    const errorLogUrl = '/gensalesinvoice/purchase-to-sales/logs/error';


    const columns = [
        {
            title: 'Select',
            dataIndex: 'InvoiceID',
            key: 'InvoiceID',
            width: 80,
            render: (id) => (
                <input
                    type="checkbox"
                    checked={selectedRowKeys.includes(id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedRowKeys([...selectedRowKeys, id]);
                        } else {
                            setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
                        }
                    }}
                />
            ),
        },
        {
            title: 'Invoice ID',
            dataIndex: 'InvoiceID',
            key: 'InvoiceID',
            sorter: (a, b) => a.InvoiceID - b.InvoiceID,
        },
        {
            title: 'Invoice Number',
            dataIndex: 'InvoiceNumber',
            key: 'InvoiceNumber',
        },
        {
            title: 'Supplier Name',
            dataIndex: 'SupplierName',
            key: 'SupplierName',
        },
        {
            title: 'Date',
            dataIndex: 'Date',
            key: 'Date',
            render: (date) => moment(date).format('YYYY-MM-DD'),
        },
    ];

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const response = await api.get('/gensalesinvoice/purchase/list-for-sales');
            setPurchaseInvoices(response.data);
        } catch (error) {
            message.error('Failed to fetch purchase invoices');
        }
        setLoading(false);
    };

    const filteredData = purchaseInvoices.filter((invoice) => {
        const matchesSupplier =
            !supplierFilter || invoice.SupplierName?.includes(supplierFilter);

        const matchesInvoiceId =
            !invoiceIdFilter ||
            String(invoice.InvoiceID).includes(invoiceIdFilter.trim());

        // If no date filter, still apply supplier + invoice filters
        if (!dateFilter) return matchesSupplier && matchesInvoiceId;

        const filterDate = dateFilter.clone().startOf('day');
        const invDate = moment(invoice.Date, 'YYYY-MM-DD').startOf('day');

        if (!invDate.isValid()) {
            console.warn('Invalid date in invoice:', invoice.InvoiceID, invoice.Date);
            return false;
        }

        let matchesDate = true;

        if (dateOperator === '>=') {
            matchesDate = invDate.isSameOrAfter(filterDate, 'day');
        } else if (dateOperator === '<=') {
            matchesDate = invDate.isSameOrBefore(filterDate, 'day');
        } else {
            matchesDate = true;
        }

        return matchesSupplier && matchesInvoiceId && matchesDate;
    });


    // handler for download all PDFs
    async function handleDownloadAll() {
        const rows = processResults?.details || [];
        if (!rows.length) return;

        // collect relative paths for all rows that have a PDF
        const pdfPaths = rows
            .filter(r => r.pdfUrl)        // use the same field you use in the column (pdfUrl)
            .map(r => r.pdfUrl);          // e.g. "uploads/generated_pdf/<Date>/Sales_Invoice_XXX.pdf"

        if (!pdfPaths.length) return;
        //alert(pdfPaths);

        try {
            const res = await fetch('/gensalesinvoice/purchase-to-sales/download-all-pdfs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pdfPaths }),
            });

            if (!res.ok) {
                console.error('Failed to download all PDFs');
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sales_invoices_${Date.now()}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading all PDFs', err);
        }
    }


    const generateTemplate = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one purchase invoice');
            return;
        }

        try {
            message.loading('Generating template...', 0);
            const response = await api.post('/gensalesinvoice/purchase-to-sales/generate-template', {
                purchaseInvoiceIds: selectedRowKeys
            }, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `sales_invoice_template_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            message.success(`Template downloaded for ${selectedRowKeys.length} invoices`);
        } catch (error) {
            message.error('Failed to generate template');
        }
    };

    const processCsvFile = async (file) => {
        setProcessing(true);
        const formData = new FormData();
        formData.append('csvFile', file);

        try {
            const response = await api.post('/gensalesinvoice/purchase-to-sales/process-csv', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setProcessResults(response.data);
            message.success(`Processed: ${response.data.successCount} success, ${response.data.failCount} failed`);

            fetchInvoices();
        } catch (error) {
            message.error('Failed to process CSV');
            console.error(error);
        }
        setProcessing(false);
    };

    const csvUploadProps = {
        name: 'csvFile',
        multiple: false,
        accept: '.csv,application/vnd.ms-excel,text/csv',
        showUploadList: false,
        fileList: csvFileList,
        beforeUpload: (file) => {
            // Basic CSV validation
            const isCSV = file.type === 'application/vnd.ms-excel' ||
                file.type === 'text/csv' ||
                file.name.toLowerCase().endsWith('.csv');

            if (!isCSV) {
                message.error('Please upload only CSV files!');
                return false;
            }

            processCsvFile(file);
            return false; // prevent default upload
        },
        onChange: ({ fileList }) => {
            setCsvFileList(fileList);
            // Clear file list after processing
            if (fileList.length === 0) {
                setCsvFileList([]);
            }
        },
    };

    const resultsColumns = [
        {
            title: 'Invoice Number',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <span>
                    {status === 'success' ? (
                        <span><FileDoneOutlined style={{ color: 'green' }} /> Success</span>
                    ) : (
                        <span><CloseCircleOutlined style={{ color: 'red' }} /> Failed</span>
                    )}
                </span>
            ),
        },
        {
            title: 'Sales Invoice ID',
            dataIndex: 'salesInvoiceId',
            key: 'salesInvoiceId',
        },
        {
            title: 'Items',
            dataIndex: 'itemsCount',
            key: 'itemsCount',
        },
        {
            title: 'PDF Download',
            dataIndex: 'pdfUrl',
            key: 'pdfUrl',
            render: (pdfUrl, record) =>
                pdfUrl ? (
                    <a
                        onClick={(e) => {
                            e.preventDefault();
                            fetch(pdfUrl)
                                .then(res => res.blob())
                                .then(blob => {
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Sales_Invoice_${record.invoiceNumber}_merged.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                })
                                .catch(err => console.error('Download failed:', err));
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    >
                        <DownloadOutlined /> Download PDF
                    </a>
                ) : (
                    <span style={{ color: 'red' }}>N/A</span>
                ),

        },

        {
            title: 'Error',
            dataIndex: 'error',
            key: 'error',
        },
    ];

    // function to stream live logs
    function LiveLogViewer({ type, title }) {
        const [lines, setLines] = React.useState([]);

        React.useEffect(() => {
            const es = new EventSource(`/gensalesinvoice/purchase-to-sales/logs/${type}/stream`);

            es.onmessage = (event) => {
                setLines(prev => [...prev, event.data]);
            };

            es.onerror = () => {
                es.close();
            };

            return () => es.close();
        }, [type]);

        return (
            <Card title={title} size="small">
      <pre
          style={{
              maxHeight: 200,
              overflow: 'auto',
              background: '#111',
              color: '#0f0',
              padding: 8,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
          }}
      >
        {lines.join('\n')}
      </pre>
            </Card>
        );
    }




    const success = Number(processResults?.successCount || 0);
    const fail = Number(processResults?.failCount || 0);

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginTop: 16 }}>
                <h3>Gen Sales Invoice folders</h3>
                <ul>
                    <li>
                        <a href={generatedFolderUrl} target="_blank" rel="noreferrer">
                            Open generated sales invoices folder
                        </a>
                    </li>
                    <li>
                        <a href={mergedFolderUrl} target="_blank" rel="noreferrer">
                            Open merged sales invoices folder
                        </a>
                    </li>
                </ul>
            </div>

            <div style={{ marginTop: 16 }}>
                <h3>Invoicing Logs</h3>
                <ul>
                    <li>
                        <a href={outLogUrl} target="_blank" rel="noreferrer">
                            View latest invoicing-out.log
                        </a>
                    </li>
                    <li>
                        <a href={errorLogUrl} target="_blank" rel="noreferrer">
                            View latest invoicing-error.log
                        </a>
                    </li>
                </ul>
            </div>

            <div>
                <p><strong>Instructions:</strong></p>
                <ul>
                    <li>1. select Purchase Invoice and click on "Generate Template"</li>
                    <li>2. Add Sales Invoice Number and PO Number, Update other Sales Invoice Fields as reqd into the template csv </li>
                    <li>3. "Upload & Process CSV" to create Sales Invoices + generate PDFs</li>
                </ul>
            </div>
            <Card title="1. Select Purchase Invoices" style={{ marginBottom: 24 }}>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={4}>
                        <Input
                            placeholder="Filter by Invoice ID"
                            value={invoiceIdFilter}
                            onChange={(e) => setInvoiceIdFilter(e.target.value)}
                            style={{ width: '100%' }}
                            allowClear
                        />
                    </Col>
                    <Col span={8}>
                        <Input
                            placeholder="Filter by Supplier"
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                            prefix={<FileExcelOutlined />}
                            style={{ width: '100%' }}
                            allowClear
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            value={dateOperator}
                            onChange={setDateOperator}
                            style={{ width: '100%' }}
                        >
                            <Option value=">=">{'>='}</Option>
                            <Option value="<=">{'<='}</Option>
                        </Select>
                    </Col>
                    <Col span={8}>
                        <DatePicker
                            placeholder="Filter by Date"
                            value={dateFilter}
                            onChange={setDateFilter}
                            style={{ width: '100%' }}
                        />
                    </Col>

                    <Col span={8}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={generateTemplate}
                                disabled={selectedRowKeys.length === 0}
                            >
                                Generate Template ({selectedRowKeys.length})
                            </Button>
                            <Button onClick={fetchInvoices} loading={loading}>
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    }}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="InvoiceID"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 800 }}
                    size="small"
                />
            </Card>

            <Card title="2. Edit CSV & Upload for Processing" style={{ marginBottom: 24 }}>
                <Space direction="vertical" style={{ width: '100%' }}>


                    <Upload {...csvUploadProps}>
                        <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            loading={processing}
                            size="large"
                            block
                        >
                            {processing ? 'Processing CSV...' : 'Upload & Process CSV'}
                        </Button>
                    </Upload>
                </Space>
            </Card>


            {processResults && (
                <Card title="3. Processing Results">
                    <Tabs defaultActiveKey="summary">
                        <TabPane tab="Summary" key="summary">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card>
                                        <h3 style={{ color: 'green' }}>{success}</h3>
                                        <p>Success</p>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card>
                                        <h3 style={{ color: 'red' }}>{fail}</h3>
                                        <p>Failed</p>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card>
                                        <h3>{success + fail}</h3>
                                        <p>Total</p>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane tab="Details" key="details">
                            {/*
          Button is always visible inside Details tab.
          It is only disabled when there are zero detail rows.
        */}
                            <div
                                style={{
                                    marginBottom: 12,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <h3 style={{ margin: 0 }}>Details</h3>
                                <button
                                    onClick={handleDownloadAll}
                                    disabled={
                                        !processResults.details ||
                                        processResults.details.length === 0
                                    }
                                >
                                    Download all PDFs
                                </button>
                            </div>

                            {processResults.details && processResults.details.length > 0 ? (
                                <Table
                                    columns={resultsColumns}
                                    dataSource={processResults.details}
                                    rowKey="invoiceNumber"
                                    pagination={false}
                                    scroll={{ x: 1000 }}
                                />
                            ) : (
                                <p>No details to display.</p>
                            )}
                        </TabPane>
                    </Tabs>

                    <div style={{ marginTop: 16 }}>
                        <Button
                            type="primary"
                            onClick={() => setProcessResults(null)}
                            icon={<FileDoneOutlined />}
                        >
                            Clear Results
                        </Button>
                    </div>
                </Card>
            )}

            {/* 4. Live logs section */}
            <div style={{ marginTop: 24, marginBottom: 24 }}>
                <h3>4. Live Server Logs</h3>
                <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                        <LiveLogViewer type="out" title="invoicing-out.log (live)" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <LiveLogViewer type="error" title="invoicing-error.log (live)" />
                    </div>
                </div>
            </div>

        </div>
    );
}
